import type { TeacherSwitchCandidate, TeacherSwitchUnavailableReason } from '@mathflat/domain/Auth'
import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'

import { Badge } from '@/component/atoms/Badge'
import { Icon } from '@/component/atoms/Icon'
import type { CommonError } from '@/dto'
import { authService } from '@/service/auth.service'
import { commonErrorMessage } from '@/utils/toast'

import S from './AcademySwitch.style'
import { useAcademySwitchCandidates } from './useAcademySwitchCandidates'

const UNAVAILABLE_REASON_TEXT: Record<TeacherSwitchUnavailableReason, string> = {
  ACADEMY_NOT_SERVICEABLE: '학원 이용이 중지된 상태예요',
  SERVICE_NOT_SUBSCRIBED: '학원이 구독하지 않았어요',
  SERVICE_ACCESS_NOT_ALLOWED: '접근 권한이 없어요',
}

const AcademySwitch = () => {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [isOpened, setIsOpened] = useState(false)
  const [isSwitching, setIsSwitching] = useState(false)

  const { candidates, currentAcademy, canSwitchAcademy, refetch } = useAcademySwitchCandidates({
    enabled: true,
  })

  useEffect(() => {
    const handleOutsideMouseDown = (event: MouseEvent) => {
      if (wrapRef.current?.contains(event.target as Node)) return
      setIsOpened(false)
    }
    document.addEventListener('mousedown', handleOutsideMouseDown)

    return () => document.removeEventListener('mousedown', handleOutsideMouseDown)
  }, [])

  if (!canSwitchAcademy || !currentAcademy) return null

  const handleItemClick = async (candidate: TeacherSwitchCandidate) => {
    if (candidate.current || !candidate.switchable || isSwitching) return

    setIsSwitching(true)
    try {
      // 성공하면 switchTeacher 가 페이지를 통째로 다시 띄우므로 이 뒤로 돌아오지 않는다.
      await authService.switchTeacher(candidate.teacherId)
    } catch (error) {
      commonErrorMessage(error as CommonError)
      // 목록을 만든 뒤 학원 상태가 바뀌어 실패할 수 있다. 사유를 갱신해 열린 채로 보여준다.
      await refetch()
    } finally {
      setIsSwitching(false)
    }
  }

  return (
    <div ref={wrapRef} css={S.wrap}>
      <button
        type="button"
        css={S.trigger}
        className={clsx(isOpened && 'opened')}
        aria-haspopup="menu"
        aria-expanded={isOpened}
        aria-label="다른 학원으로 전환"
        onClick={() => setIsOpened((prev) => !prev)}
      >
        <span css={S.triggerLabel}>{currentAcademy.academyName}</span>
        <Icon name="chevron_down" size={16} color="var(--Colors-Gray-900)" aria-hidden="true" />
      </button>

      {isOpened && (
        <div css={S.menu} role="menu">
          <span css={S.menuHeader}>다른 학원 선택</span>
          {candidates.map((candidate) => {
            const isActionable = !candidate.current && candidate.switchable && !isSwitching

            return (
              <button
                key={candidate.teacherId}
                type="button"
                role="menuitem"
                css={S.item}
                className={clsx(isActionable && 'actionable')}
                disabled={!candidate.current && !candidate.switchable}
                // 현재 학원은 눌러도 동작하지 않지만 disabled 로 빼지 않는다. 목록에서 어디에
                // 있는지 읽히려면 포커스가 닿아야 한다.
                aria-disabled={candidate.current}
                onClick={() => handleItemClick(candidate)}
              >
                <span css={S.itemText}>
                  <span css={S.academyName}>{candidate.academyName}</span>
                  {candidate.unavailableReason && (
                    <span css={S.unavailableReason}>
                      {/* BE 가 사유를 늘리면 조회가 비는데, 눌리지 않는 이유까지 사라지면
                          목록에 남겨 둔 뜻이 없어진다. */}
                      {UNAVAILABLE_REASON_TEXT[candidate.unavailableReason] ??
                        '지금은 전환할 수 없어요'}
                    </span>
                  )}
                </span>
                <span css={S.itemAside}>
                  {candidate.current ? (
                    <Badge
                      size="xs"
                      radius="circle"
                      backgroundColor="var(--Colors-Gray-200)"
                      color="var(--Colors-Gray-600)"
                    >
                      이용 중
                    </Badge>
                  ) : (
                    candidate.switchable && (
                      <Icon
                        name="chevron_right"
                        size={16}
                        color="var(--Colors-Gray-600)"
                        aria-hidden="true"
                      />
                    )
                  )}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default AcademySwitch
